/* global Office */

import {
  FINANCE_DOCUMENT_CLASSIFICATION,
  FINANCE_DOCUMENT_PRIVACY_SETTINGS_KEY,
  FINANCE_OFFICE_RECEIPT_STATUS,
  FINANCE_OFFICE_SCHEMA_VERSION,
} from './contracts';

const MAX_OPERATION_RECEIPTS = 256;
const RECEIPT_KEYS = Object.freeze([
  'app',
  'command',
  'completedAt',
  'documentId',
  'mutationCount',
  'operationBinding',
  'operationId',
  'schemaVersion',
  'startedAt',
  'status',
  'stepId',
  'targetId',
  'targetType',
]);
const OPERATION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class FinanceDocumentPrivacyError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'FinanceDocumentPrivacyError';
    this.code = code;
  }
}

function privacyError(code, message) {
  return new FinanceDocumentPrivacyError(code, message);
}

function strictRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function validDateOrNull(value) {
  return value === null || (
    typeof value === 'string' &&
    value.length <= 40 &&
    Number.isFinite(Date.parse(value))
  );
}

function secureDocumentId(
  cryptoSource = typeof window !== 'undefined' ? window.crypto : null
) {
  if (!cryptoSource || typeof cryptoSource.getRandomValues !== 'function') {
    throw privacyError(
      'finance_document_identity_unavailable',
      'Secure Office document identity generation is unavailable'
    );
  }
  if (typeof cryptoSource.randomUUID === 'function') {
    return cryptoSource.randomUUID();
  }
  const bytes = new Uint8Array(16);
  cryptoSource.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = Array.from(
    bytes,
    (byte) => byte.toString(16).padStart(2, '0')
  ).join('');
  return [
    value.slice(0, 8),
    value.slice(8, 12),
    value.slice(12, 16),
    value.slice(16, 20),
    value.slice(20),
  ].join('-');
}

const FILE_IDENTITY_HASH_RE = /^sha256:[0-9a-f]{64}$/;

function defaultState(documentId, fileIdentityHash = null) {
  return {
    schemaVersion: FINANCE_OFFICE_SCHEMA_VERSION,
    documentId,
    fileIdentityHash,
    licensedData: false,
    classification: null,
    taintedAt: null,
    receipts: {},
  };
}

function validateReceipt(receipt, documentId) {
  if (!strictRecord(receipt)) {
    throw privacyError(
      'finance_document_privacy_invalid',
      'Finance Office receipt metadata is invalid'
    );
  }
  const keys = Object.keys(receipt).sort();
  if (
    keys.length !== RECEIPT_KEYS.length ||
    keys.some((key, index) => key !== RECEIPT_KEYS[index])
  ) {
    throw privacyError(
      'finance_document_privacy_invalid',
      'Finance Office receipt metadata contains unsupported fields'
    );
  }
  if (
    receipt.schemaVersion !== FINANCE_OFFICE_SCHEMA_VERSION ||
    receipt.documentId !== documentId ||
    !Object.values(FINANCE_OFFICE_RECEIPT_STATUS).includes(receipt.status) ||
    !['powerpoint', 'excel', 'word'].includes(receipt.app) ||
    typeof receipt.command !== 'string' ||
    receipt.command.length > 80 ||
    typeof receipt.operationId !== 'string' ||
    !OPERATION_ID_RE.test(receipt.operationId) ||
    typeof receipt.operationBinding !== 'string' ||
    !OPERATION_ID_RE.test(receipt.operationBinding) ||
    receipt.operationBinding !== receipt.operationBinding.toLowerCase() ||
    typeof receipt.stepId !== 'string' ||
    receipt.stepId.length > 64 ||
    !['slide', 'worksheet_range', 'content_control'].includes(receipt.targetType) ||
    typeof receipt.targetId !== 'string' ||
    !/^[A-Za-z0-9][A-Za-z0-9._:{}!$-]{0,255}$/.test(receipt.targetId) ||
    !Number.isSafeInteger(receipt.mutationCount) ||
    receipt.mutationCount < 0 ||
    receipt.mutationCount > 10_000 ||
    !validDateOrNull(receipt.startedAt) ||
    !validDateOrNull(receipt.completedAt)
  ) {
    throw privacyError(
      'finance_document_privacy_invalid',
      'Finance Office receipt metadata is invalid'
    );
  }
  return { ...receipt };
}

function validateState(value) {
  if (!strictRecord(value)) {
    throw privacyError(
      'finance_document_privacy_invalid',
      'Finance document privacy state is invalid'
    );
  }
  const allowedKeys = [
    'classification',
    'documentId',
    'fileIdentityHash',
    'licensedData',
    'receipts',
    'schemaVersion',
    'taintedAt',
  ];
  const keys = Object.keys(value).sort();
  const legacyKeys = allowedKeys.filter((key) => key !== 'fileIdentityHash');
  if (
    !(
      (
        keys.length === allowedKeys.length &&
        keys.every((key, index) => key === allowedKeys[index])
      ) ||
      (
        keys.length === legacyKeys.length &&
        keys.every((key, index) => key === legacyKeys[index])
      )
    )
  ) {
    throw privacyError(
      'finance_document_privacy_invalid',
      'Finance document privacy state contains unsupported fields'
    );
  }
  if (
    value.schemaVersion !== FINANCE_OFFICE_SCHEMA_VERSION ||
    typeof value.documentId !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.documentId) ||
    !(
      value.fileIdentityHash === undefined ||
      value.fileIdentityHash === null ||
      (
        typeof value.fileIdentityHash === 'string' &&
        FILE_IDENTITY_HASH_RE.test(value.fileIdentityHash)
      )
    ) ||
    typeof value.licensedData !== 'boolean' ||
    !strictRecord(value.receipts) ||
    Object.keys(value.receipts).length > MAX_OPERATION_RECEIPTS
  ) {
    throw privacyError(
      'finance_document_privacy_invalid',
      'Finance document privacy state is invalid'
    );
  }
  if (value.licensedData) {
    if (
      value.classification !== FINANCE_DOCUMENT_CLASSIFICATION ||
      !validDateOrNull(value.taintedAt) ||
      value.taintedAt === null
    ) {
      throw privacyError(
        'finance_document_privacy_invalid',
        'Finance document licensed-data marker is invalid'
      );
    }
  } else if (value.classification !== null || value.taintedAt !== null) {
    throw privacyError(
      'finance_document_privacy_invalid',
      'Finance document privacy marker is inconsistent'
    );
  }
  const receipts = {};
  for (const [operationId, receipt] of Object.entries(value.receipts)) {
    if (operationId !== receipt?.operationId) {
      throw privacyError(
        'finance_document_privacy_invalid',
        'Finance Office receipt key is invalid'
      );
    }
    receipts[operationId] = validateReceipt(receipt, value.documentId);
  }
  return {
    schemaVersion: FINANCE_OFFICE_SCHEMA_VERSION,
    documentId: value.documentId,
    fileIdentityHash: value.fileIdentityHash || null,
    licensedData: value.licensedData,
    classification: value.classification,
    taintedAt: value.taintedAt,
    receipts,
  };
}

function cloneState(state) {
  return {
    ...state,
    receipts: Object.fromEntries(
      Object.entries(state.receipts).map(([key, receipt]) => [key, { ...receipt }])
    ),
  };
}

function asyncResultSucceeded(result) {
  const expected = typeof Office !== 'undefined'
    ? Office.AsyncResultStatus?.Succeeded
    : null;
  return result?.status === expected ||
    result?.status === 'succeeded' ||
    result?.status === 'Succeeded';
}

function runSettingsAsync(settings, method) {
  if (typeof settings?.[method] !== 'function') {
    if (method === 'refreshAsync') return Promise.resolve();
    return Promise.reject(
      privacyError(
        'finance_document_privacy_unavailable',
        'Office document settings persistence is unavailable'
      )
    );
  }
  return new Promise((resolve, reject) => {
    settings[method]((result) => {
      if (asyncResultSucceeded(result)) {
        resolve();
        return;
      }
      reject(
        privacyError(
          'finance_document_privacy_unavailable',
          'Office document settings could not be persisted'
        )
      );
    });
  });
}

function resolveSettings(explicitSettings) {
  const settings = explicitSettings ||
    (typeof Office !== 'undefined' ? Office.context?.document?.settings : null);
  if (
    !settings ||
    typeof settings.get !== 'function' ||
    typeof settings.set !== 'function' ||
    typeof settings.saveAsync !== 'function'
  ) {
    return null;
  }
  return settings;
}

function resolveCrypto(explicitCrypto) {
  return explicitCrypto ||
    (typeof window !== 'undefined' ? window.crypto : null);
}

async function sha256(value, explicitCrypto) {
  const cryptoSource = resolveCrypto(explicitCrypto);
  if (
    !cryptoSource?.subtle ||
    typeof cryptoSource.subtle.digest !== 'function' ||
    typeof TextEncoder === 'undefined'
  ) {
    throw privacyError(
      'finance_document_identity_unavailable',
      'Secure Office file identity hashing is unavailable'
    );
  }
  const digest = await cryptoSource.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(value)
  );
  return `sha256:${Array.from(
    new Uint8Array(digest),
    (byte) => byte.toString(16).padStart(2, '0')
  ).join('')}`;
}

function getFileProperties() {
  const document = typeof Office !== 'undefined'
    ? Office.context?.document
    : null;
  if (typeof document?.getFilePropertiesAsync !== 'function') {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    document.getFilePropertiesAsync((result) => {
      if (!asyncResultSucceeded(result)) {
        resolve(null);
        return;
      }
      const url = typeof result?.value?.url === 'string'
        ? result.value.url.trim()
        : '';
      resolve(url || null);
    });
  });
}

async function defaultFileIdentityProvider(app, cryptoSource) {
  if (!['powerpoint', 'excel', 'word'].includes(app)) return null;
  const url = await getFileProperties();
  if (!url) return null;
  return sha256(`${app}\u0000${url}`, cryptoSource);
}

function licensedState(documentId, fileIdentityHash, taintedAt) {
  return {
    ...defaultState(documentId, fileIdentityHash),
    licensedData: true,
    classification: FINANCE_DOCUMENT_CLASSIFICATION,
    taintedAt,
  };
}

export class FinanceDocumentPrivacy {
  constructor({
    settings = null,
    app = null,
    evidenceDetector = async () => false,
    fileIdentityProvider = null,
    cryptoSource = null,
    idFactory = secureDocumentId,
    now = () => new Date(),
  } = {}) {
    this.settings = resolveSettings(settings);
    this.app = app;
    this.evidenceDetector = evidenceDetector;
    this.fileIdentityProvider = fileIdentityProvider ||
      (() => defaultFileIdentityProvider(this.app, cryptoSource));
    this.idFactory = idFactory;
    this.now = now;
    this.state = null;
    this.persistenceAvailable = Boolean(this.settings);
    this.persistenceBlocked = false;
    this.queue = Promise.resolve();
  }

  async _loadFresh() {
    if (!this.settings) {
      throw privacyError(
        'finance_document_privacy_unavailable',
        'Office document settings persistence is unavailable'
      );
    }
    await runSettingsAsync(this.settings, 'refreshAsync');
    const raw = this.settings.get(FINANCE_DOCUMENT_PRIVACY_SETTINGS_KEY);
    if (raw === undefined || raw === null || raw === '') {
      return null;
    }
    let parsed;
    try {
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      throw privacyError(
        'finance_document_privacy_invalid',
        'Finance document privacy state could not be parsed'
      );
    }
    return validateState(parsed);
  }

  async _save(state) {
    if (!this.settings) {
      throw privacyError(
        'finance_document_privacy_unavailable',
        'Office document settings persistence is unavailable'
      );
    }
    const validated = validateState(state);
    this.settings.set(
      FINANCE_DOCUMENT_PRIVACY_SETTINGS_KEY,
      JSON.stringify(validated)
    );
    await runSettingsAsync(this.settings, 'saveAsync');
    this.persistenceAvailable = true;
    this.persistenceBlocked = false;
    this.state = validated;
    return cloneState(validated);
  }

  _serialize(operation) {
    const run = this.queue.then(operation, operation);
    this.queue = run.catch(() => {});
    return run;
  }

  async _currentFileIdentityHash() {
    try {
      const value = await this.fileIdentityProvider();
      return typeof value === 'string' && FILE_IDENTITY_HASH_RE.test(value)
        ? value
        : null;
    } catch {
      return null;
    }
  }

  async _licensedEvidence() {
    try {
      return await this.evidenceDetector() === true;
    } catch {
      return null;
    }
  }

  async _assertCurrentFileIdentity(state, expectedDocumentId = null) {
    const currentFileIdentityHash = await this._currentFileIdentityHash();
    if (
      !state?.fileIdentityHash ||
      !currentFileIdentityHash ||
      currentFileIdentityHash !== state.fileIdentityHash ||
      (
        expectedDocumentId &&
        state.documentId !== expectedDocumentId
      ) ||
      !this.settings ||
      this.persistenceAvailable !== true ||
      this.persistenceBlocked
    ) {
      throw privacyError(
        expectedDocumentId && state?.documentId !== expectedDocumentId
          ? 'finance_document_identity_mismatch'
          : 'finance_document_identity_unavailable',
        'The current Office file identity could not be verified'
      );
    }
    return state;
  }

  async _saveOrRemember(state) {
    if (!this.settings) {
      this.persistenceAvailable = false;
      this.state = validateState(state);
      return cloneState(this.state);
    }
    try {
      return await this._save(state);
    } catch {
      this.persistenceAvailable = false;
      this.persistenceBlocked = true;
      this.state = validateState(state);
      return cloneState(this.state);
    }
  }

  async _resolveState() {
    const currentFileIdentityHash = await this._currentFileIdentityHash();
    let existing = null;
    let invalidMarker = false;
    try {
      existing = await this._loadFresh();
      this.persistenceAvailable = !this.persistenceBlocked;
    } catch (error) {
      this.persistenceAvailable = false;
      invalidMarker = error?.code === 'finance_document_privacy_invalid';
      if (!invalidMarker && error?.code !== 'finance_document_privacy_unavailable') {
        throw error;
      }
    }

    if (
      existing &&
      existing.fileIdentityHash &&
      currentFileIdentityHash &&
      existing.fileIdentityHash === currentFileIdentityHash
    ) {
      this.state = existing;
      return cloneState(existing);
    }

    const evidence = await this._licensedEvidence();
    const now = this.now().toISOString();
    if (existing) {
      if (!currentFileIdentityHash) {
        this.state = existing;
        return cloneState(existing);
      }
      const shouldRemainLicensed = existing.licensedData || evidence !== false;
      const rotated = shouldRemainLicensed
        ? licensedState(this.idFactory(), currentFileIdentityHash, existing.taintedAt || now)
        : defaultState(this.idFactory(), currentFileIdentityHash);
      return this._saveOrRemember(rotated);
    }

    if (
      this.state &&
      this.state.fileIdentityHash === currentFileIdentityHash &&
      !invalidMarker
    ) {
      return cloneState(this.state);
    }

    const mustFailClosed = invalidMarker || evidence !== false;
    const initial = mustFailClosed
      ? licensedState(this.idFactory(), currentFileIdentityHash, now)
      : defaultState(this.idFactory(), currentFileIdentityHash);
    return this._saveOrRemember(initial);
  }

  ensureDocumentIdentity() {
    return this._serialize(() => this._resolveState());
  }

  getState() {
    return this._serialize(() => this._resolveState());
  }

  assertCurrentFileIdentity(expectedDocumentId = null) {
    return this._serialize(async () => {
      const current = await this._resolveState();
      await this._assertCurrentFileIdentity(current, expectedDocumentId);
      this.state = current;
      return cloneState(current);
    });
  }

  async getWireMarker() {
    const state = await this.getState();
    return Object.freeze({
      version: FINANCE_OFFICE_SCHEMA_VERSION,
      documentId: state.documentId,
      state: state.licensedData ? 'licensed_data' : 'clean',
      providers: Object.freeze(state.licensedData ? ['daloopa'] : []),
      taintedAt: state.taintedAt,
    });
  }

  async getConnectorWireMarker() {
    const state = await this.getState();
    const currentFileIdentityHash = await this._currentFileIdentityHash();
    if (
      !state.fileIdentityHash ||
      !currentFileIdentityHash ||
      currentFileIdentityHash !== state.fileIdentityHash ||
      !this.settings ||
      this.persistenceAvailable !== true ||
      this.persistenceBlocked
    ) {
      throw privacyError(
        'finance_document_identity_unavailable',
        'A stable persisted Office file identity is required'
      );
    }
    return Object.freeze({
      version: FINANCE_OFFICE_SCHEMA_VERSION,
      documentId: state.documentId,
      state: state.licensedData ? 'licensed_data' : 'clean',
      providers: Object.freeze(state.licensedData ? ['daloopa'] : []),
      taintedAt: state.taintedAt,
    });
  }

  markLicensedData(expectedDocumentId = null) {
    return this._serialize(async () => {
      const current = await this._resolveState();
      await this._assertCurrentFileIdentity(current, expectedDocumentId);
      if (current.licensedData) {
        this.state = current;
        return cloneState(current);
      }
      return this._save({
        ...current,
        licensedData: true,
        classification: FINANCE_DOCUMENT_CLASSIFICATION,
        taintedAt: this.now().toISOString(),
      });
    });
  }

  getReceipt(operationId) {
    return this._serialize(async () => {
      const current = await this._resolveState();
      if (!current) return null;
      this.state = current;
      const receipt = current.receipts[operationId];
      return receipt ? { ...receipt } : null;
    });
  }

  putReceipt(receipt) {
    return this._serialize(async () => {
      const current = await this._resolveState();
      if (!current) {
        throw privacyError(
          'finance_document_identity_mismatch',
          'Finance Office document identity is not initialized'
        );
      }
      await this._assertCurrentFileIdentity(
        current,
        receipt?.documentId || null
      );
      const safeReceipt = validateReceipt(receipt, current.documentId);
      const previous = current.receipts[safeReceipt.operationId];
      if (
        previous &&
        (
          previous.documentId !== safeReceipt.documentId ||
          previous.stepId !== safeReceipt.stepId ||
          previous.operationBinding !== safeReceipt.operationBinding ||
          previous.app !== safeReceipt.app ||
          previous.command !== safeReceipt.command ||
          previous.targetId !== safeReceipt.targetId ||
          previous.targetType !== safeReceipt.targetType
        )
      ) {
        throw privacyError(
          'finance_office_operation_conflict',
          'Finance Office operation identity does not match its prior receipt'
        );
      }
      if (
        previous &&
        [
          FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED,
          FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED_AFTER_CANCEL,
        ].includes(previous.status) &&
        previous.status !== safeReceipt.status
      ) {
        throw privacyError(
          'finance_office_operation_conflict',
          'A committed Finance Office receipt is immutable'
        );
      }
      if (!previous && Object.keys(current.receipts).length >= MAX_OPERATION_RECEIPTS) {
        throw privacyError(
          'finance_office_receipt_capacity_reached',
          'This Office file has reached the private operation receipt limit'
        );
      }
      return this._save({
        ...current,
        receipts: {
          ...current.receipts,
          [safeReceipt.operationId]: safeReceipt,
        },
      }).then((next) => ({ ...next.receipts[safeReceipt.operationId] }));
    });
  }
}

export default FinanceDocumentPrivacy;
